/**
 * 랜딩 "체험 계정 신청하기" 모달 폼.
 * 지역/성함/이메일/전화번호/학교 구분/학년(+선택 문의사항)을 받아 Lambda 엔드포인트로 전송한다.
 * 엔드포인트가 아직 비어 있으면 전송 없이 완료 화면만 띄운다.
 */
import React, { useEffect, useRef, useState } from "react";
import useDialog from "./useDialog";
import { trackLandingEvent } from "./config";
import { formatPhoneAsYouType } from "@/utils/formatPhoneAsYouType";
import {
  ApplyForm as Form,
  DEFAULT_APPLY_NOTE as DEFAULT_NOTE,
  EMPTY_APPLY_FORM as EMPTY,
  REGIONS,
  SCHOOL_TYPES,
  buildApplyPayload,
  gradeRange,
  isApplyFormComplete,
  submitApply,
  validateApplyForm,
} from "./applyForm";

/** 히어로 "체험 계정 신청하기" → 도입 상담 신청 폼. */
export default function LandingApplyModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { ref, onBackdropClick } = useDialog(open, onClose);
  const emailRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [note, setNote] = useState(DEFAULT_NOTE);
  const [isError, setIsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // 완료 화면을 본 뒤 다시 열면 입력 폼으로 되돌립니다.
  useEffect(() => {
    if (!open) return;
    setDone(false);
    setForm(EMPTY);
    setNote(DEFAULT_NOTE);
    setIsError(false);
    setSubmitting(false);
  }, [open]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const [minGrade, maxGrade] = gradeRange(form.schoolType);
  const grades = Array.from({ length: maxGrade - minGrade + 1 }, (_, i) => minGrade + i);

  const onSchoolTypeChange = (value: string) => {
    const [nextMin, nextMax] = gradeRange(value);
    const n = Number(form.grade);
    setForm((prev) => ({
      ...prev,
      schoolType: value,
      // 학교 구분을 바꾸면 이전 학년이 범위 밖일 수 있으므로 유효할 때만 유지합니다.
      grade: prev.grade && n >= nextMin && n <= nextMax ? prev.grade : "",
    }));
  };

  const emailValid = () => emailRef.current?.checkValidity() ?? false;

  /**
   * 필수 항목이 다 차기 전에는 제출 버튼을 비활성처럼 보여줍니다.
   * 실제 disabled 를 걸면 클릭이 막혀 무엇이 빠졌는지 알려줄 수 없어, 모양만 바꿉니다.
   */
  const isComplete = () => isApplyFormComplete(form, emailValid());

  const fail = (msg: string) => {
    setNote(msg);
    setIsError(true);
  };

  const resetNote = () => {
    setNote(DEFAULT_NOTE);
    setIsError(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const invalid = validateApplyForm(form, emailValid());
    if (invalid) return fail(invalid);

    const payload = buildApplyPayload(form, "landing");

    resetNote();
    setSubmitting(true);
    trackLandingEvent("generate_lead", {
      form: "landing_contact",
      region: form.region,
      school_type: form.schoolType,
      grade: form.grade,
    });

    const result = await submitApply(payload);
    if (result.ok) setDone(true);
    else fail(result.error);
    setSubmitting(false);
  };

  return (
    <dialog className="modal" ref={ref} onClick={onBackdropClick} aria-labelledby="apply-title">
      <div className={`modal-card${done ? " is-done" : ""}`}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="m5 5 10 10M15 5 5 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <form className="modal-body" onSubmit={onSubmit} noValidate>
          <h2 className="modal-title" id="apply-title">
            체험 계정 신청하기
          </h2>
          <p className="modal-sub">연락처를 남겨주시면 담당자가 도입 절차를 안내드립니다.</p>

          <div className="modal-grid">
            <div className="modal-field">
              <label htmlFor="region">
                지역{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id="region"
                name="region"
                required
                autoComplete="address-level1"
                value={form.region}
                onChange={(e) => set("region", e.target.value)}
              >
                <option value="" disabled>
                  지역 선택
                </option>
                {REGIONS.map((region) => (
                  <option key={region}>{region}</option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label htmlFor="name">
                성함{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                autoFocus
                placeholder="김해봄"
                autoComplete="name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="email">
                이메일{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="haebom@haebom.ai.kr"
                autoComplete="email"
                ref={emailRef}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="phone">
                전화번호{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                inputMode="tel"
                placeholder="010-1234-5678"
                maxLength={13}
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => set("phone", formatPhoneAsYouType(e.target.value))}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="school-type">
                학교 구분{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id="school-type"
                name="schoolType"
                required
                autoComplete="off"
                value={form.schoolType}
                onChange={(e) => onSchoolTypeChange(e.target.value)}
              >
                <option value="" disabled>
                  초/중/고 선택
                </option>
                {SCHOOL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label htmlFor="grade">
                학년{" "}
                <span className="req" aria-hidden="true">
                  *
                </span>
              </label>
              <select
                id="grade"
                name="grade"
                required
                autoComplete="off"
                disabled={!maxGrade}
                value={form.grade}
                onChange={(e) => set("grade", e.target.value)}
              >
                <option value="" disabled>
                  {maxGrade ? "학년 선택" : "학교 구분 먼저 선택"}
                </option>
                {grades.map((n) => (
                  <option key={n} value={String(n)}>
                    {n}학년
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-field full">
              <label htmlFor="message">
                추가 문의사항 <span className="opt">(선택)</span>
                <span className="counter">{form.message.length}/300</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={3}
                maxLength={300}
                autoComplete="off"
                placeholder="추가 문의사항 문의할 내용이 있으면 작성해 주세요."
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            </div>
          </div>

          <label className="consent">
            {/* 새로고침 시 브라우저가 동의 상태를 복원하지 않도록 (동의는 매번 명시적으로) */}
            <input
              type="checkbox"
              name="consent"
              required
              autoComplete="off"
              checked={form.consent}
              onChange={(e) => set("consent", e.target.checked)}
            />
            <span>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener"
                aria-label="개인정보 수집·이용 안내 (새 창 열림)"
              >
                개인정보 수집·이용
              </a>
              에 동의합니다.
            </span>
          </label>

          <p className={`form-note${isError ? " is-error" : ""}`} role={isError ? "alert" : undefined}>
            {note}
          </p>

          <button
            type="submit"
            disabled={submitting}
            className={isComplete() ? undefined : "is-incomplete"}
            aria-disabled={isComplete() ? "false" : "true"}
          >
            {submitting ? "전송 중…" : "신청하기"}
          </button>
        </form>

        <div className="modal-done">
          <span className="check" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
              <path
                d="m4.5 10.5 3.5 3.5 7.5-8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="t-h3">접수했습니다.</p>
          <p className="t-body" style={{ fontSize: ".94rem" }}>
            영업일 기준 3일 이내에 남겨주신 연락처로 안내드릴게요.
          </p>
          <button type="button" className="modal-done-btn" onClick={onClose}>
            확인
          </button>
        </div>
      </div>
    </dialog>
  );
}
